import { type Authority, GROUP_AUTHORITIES } from "./authorities";
import type { ResolvedAccess } from "./authorities";

/**
 * L0–L4, the level vocabulary the design speaks.
 *
 * This is a RENAMING, not a new model — the four groups are already a total
 * order, which the test in this file asserts rather than assumes:
 *
 *   admin-readonly  ⊂ admin-support   adds block:write
 *   admin-support   ⊂ admin-operator  adds tenant/quota/settings:write
 *   admin-operator  ⊂ admin-owner     adds quota:force, service:restart
 *
 * Each group is a strict superset of the one before it, so "higher level" is a
 * real relation over them and L1..L4 name the rungs that already exist. L0 is
 * what having no group looks like — genuinely below the view threshold, not a
 * group anyone is put in.
 *
 * A level is still a DERIVED LABEL and never a decision input: every `can()`
 * asks the AUTHORITY map, because that is what the token carries and what the
 * API gateway is to enforce. What the ladder buys is that the design's
 * "needs L3" reads as a fact about this console rather than a foreign
 * vocabulary an operator has to translate.
 */
export const LEVELS = ["L0", "L1", "L2", "L3", "L4"] as const;
export type Level = (typeof LEVELS)[number];

export const LEVEL_NAME: Record<Level, string> = {
  L0: "Auditor",
  L1: "Viewer",
  L2: "Operator",
  L3: "Admin",
  L4: "Owner",
};

/**
 * Which group maps to which level, and why.
 *
 * Read down, not across: the level is the highest one whose group the operator
 * is in. Multiple groups take the MAXIMUM, matching `resolveAccess`, which
 * unions authorities and never applies a deny rule.
 */
export const GROUP_LEVEL: Record<string, Level> = {
  // Reads only — the design's L1 minimum for "View tenant, evidence, effective values".
  "admin-readonly": "L1",
  // Adds block:write, the 2am incident lever. The design's L2 covers exactly
  // the per-tenant writes: tier, block, override, beta grant.
  "admin-support": "L2",
  // Adds tier/quota/settings writes. Fleet-scoped writes are L3 in the design,
  // and this is the group that holds them.
  "admin-operator": "L3",
  // quota:force and service:restart. The design's L4 is the only level that may
  // grant or revoke a level, which is this group's real-world equivalent.
  "admin-owner": "L4",
};

/** No group is not L1-with-nothing — it is genuinely below the view threshold. */
export function levelOf(access: Pick<ResolvedAccess, "groups">): Level {
  let best: Level = "L0";
  for (const g of access.groups) {
    const lvl = GROUP_LEVEL[g];
    if (lvl && LEVELS.indexOf(lvl) > LEVELS.indexOf(best)) best = lvl;
  }
  return best;
}

/**
 * The authorities a level implies, for explaining a level rather than gating on
 * one. Derived from the same table the gate reads, so the two cannot drift.
 */
export function authoritiesOfLevel(level: Level): readonly Authority[] {
  const group = Object.keys(GROUP_LEVEL).find((g) => GROUP_LEVEL[g] === level);
  return group ? (GROUP_AUTHORITIES[group] ?? []) : [];
}
